import { useEffect, useState } from 'react';
import parse from 'html-react-parser';
import moment from 'moment';
import Icon from '@/customercenter-ui/icons/help';
import InfolIcon from '@/customercenter-ui/icons/info';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/customercenter-ui/ui/accordion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/customercenter-ui/ui/dialog';
import { useCustomObjects } from '@/frontastic';
import { mapLocaleToMeaningfulFormat } from '@/helpers/utils/i18n';
import Loader from '../../customercenter-ui/components/ui/loader';

type ObjectType = {
  [key: string]: string[];
};

const ExcludedBrandTooltip = ({
  excludedBrandsContent,
  className,
  showRedIcon = false,
  excludedBrand = false,
  setIsExcludedBrands,
}: any) => {
  const { getExcludedBrandsData } = useCustomObjects();
  const [brands, setBrands] = useState<ObjectType>({});
  const [open, setOpen] = useState(excludedBrand);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchExcludedBrandsDataStorage = () => {
    let excludedBrandsDataFromStorage;
    const excludedBrandsDataLocalStorage = localStorage.getItem('excludedBrandsData');
    if (excludedBrandsDataLocalStorage) {
      try {
        const excludedBrandsData = JSON.parse(excludedBrandsDataLocalStorage);
        if (excludedBrandsData) {
          const { data, isSucess, date } = excludedBrandsData;
          if (isSucess && date && data) {
            const dateObj = moment(date);
            const currentDate = new Date();
            const currentDateObj = moment(currentDate?.getTime());
            const isSameDate = dateObj.isSame(currentDateObj, 'day');
            const dateObjHour = dateObj.hours();
            const currentDateHour = currentDateObj.hours();
            if (isSameDate && dateObjHour === currentDateHour) {
              excludedBrandsDataFromStorage = data;
            }
          }
        }
      } catch (error) {
        console.error('Error while parsing deliveryDateData');
      }
    }
    return excludedBrandsDataFromStorage;
  };

  const fetchExcludedBrandsData = async () => {
    setIsLoading(true);
    try {
      const excludedBrandsDataFromStorage = fetchExcludedBrandsDataStorage();
      if (excludedBrandsDataFromStorage) {
        setBrands(excludedBrandsDataFromStorage);
      } else {
        const excludedBrands = await getExcludedBrandsData();
        if (excludedBrands && Object.entries(excludedBrands)?.length > 0) {
          const excludedBrandsData = {
            data: excludedBrands,
            isSucess: true,
            date: new Date(),
          };
          localStorage.setItem('excludedBrandsData', JSON.stringify(excludedBrandsData));
          setBrands(excludedBrands);
        }
      }
    } catch (error) {
      console.error('Error while fetching excluded brand data.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchExcludedBrandsData();
  }, []);

  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        if (!value) {
          setIsExcludedBrands?.(false);
          setOpen?.(false);
        }
      }}
    >
      <DialogTrigger
        onClick={() => {
          setOpen?.(true);
        }}
      >
        {showRedIcon ? <InfolIcon className={className} /> : <Icon />}
      </DialogTrigger>
      <DialogContent className="scrollbar max-h-[calc(95%_-_20px)] max-w-[794px] overflow-y-auto px-0 ">
        <DialogHeader>
          <DialogTitle className="px-5 text-left text-2xl font-bold">
            {mapLocaleToMeaningfulFormat().excludedBrand}
          </DialogTitle>
          <p className="px-5 pb-7 text-left lg:pb-4">{mapLocaleToMeaningfulFormat().brandDesc}</p>
        </DialogHeader>
        {isLoading ? (
          <Loader />
        ) : (
          <>
            <section className="hidden max-h-[1500px] flex-col flex-wrap gap-x-4 px-5 lg:flex">
              {Object.entries(brands)
                .sort(([a], [b]) => a.localeCompare(b))
                .map(([key, values]) => (
                  <section key={key} className="mb-6">
                    <h4 className="text-sm font-bold text-black">{key}</h4>
                    {values.map((brandName) => (
                      <ul key={brandName}>
                        <li className="text-left text-sm leading-5 text-black">{brandName}</li>
                      </ul>
                    ))}
                  </section>
                ))}
            </section>
            <section className="my-8 block border-t border-foggyGray lg:mb-20 lg:hidden">
              <Accordion type="single" collapsible className="w-full">
                {Object.entries(brands)
                  .sort(([a], [b]) => a.localeCompare(b))
                  .map(([key, values], index) => (
                    <AccordionItem key={`item-${key}`} value={`item-${index}`} className="border-b border-foggyGray">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline">
                        <h2 className="text-lg font-bold" id={`letter_${key}`}>
                          {key}
                        </h2>
                      </AccordionTrigger>
                      <AccordionContent className="px-4">
                        <section className="grid grid-rows-1 gap-4 lg:grid-cols-4 lg:gap-y-2">
                          {values.map((brandName) => (
                            <ul key={brandName}>
                              <li className="text-left text-sm leading-5 text-black">{brandName}</li>
                            </ul>
                          ))}
                        </section>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
              </Accordion>
            </section>
          </>
        )}
        <section>{excludedBrandsContent ? parse(excludedBrandsContent) : <></>}</section>
      </DialogContent>
    </Dialog>
  );
};

export default ExcludedBrandTooltip;
